// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
import 'babel-polyfill';
import Vue from 'vue';
import Vuex from 'vuex';
import Vuetify from 'vuetify';
import App from './App';
import router from './router';
import store from '@/vuex';
import moment from 'moment';
import VueChartkick from 'vue-chartkick';
import Chart from 'chart.js';
import isEmpty from 'lodash/isEmpty';
import 'vuetify/dist/vuetify.min.css';
import './assets/style/vuetify-overrides.css';
import './styles/globals.css';
import Vue2Filters from 'vue2-filters';
import VueAxios from 'vue-axios';
import Vuelidate from 'vuelidate';
import Quill from 'quill';
import VueQuillEditor from 'vue-quill-editor';
import axios from 'axios';
import '@/firebase/';
import firebase from 'firebase/app';
import * as Sentry from '@sentry/vue';
import { BrowserTracing } from '@sentry/tracing';
import upperFirst from 'lodash/upperFirst';
import camelCase from 'lodash/camelCase';
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';
import parse from 'url-parse';
import VueI18n from 'vue-i18n';
import { languages } from './translations';
// import posthog from 'posthog-js';
import VueCompositionAPI from '@vue/composition-api';
import VueSignaturePad from 'vue-signature-pad';
import VueVirtualScroller from 'vue-virtual-scroller';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';
import VueMask from 'v-mask';

let Link = Quill.import('formats/link');
Link.sanitize = function customSanitizeLinkInput(linkValueInput) {
  let val = linkValueInput;
  if (/^\w+:/.test(val));
  else if (!/^https?:/.test(val)) val = 'https://' + val;

  return val;
};
window.Quill = Quill;

Vue.use(VueCompositionAPI);
Vue.use(VueSignaturePad);
Vue.use(VueVirtualScroller);
Vue.use(VueMask);

Vue.prototype.$firebase = firebase;

Vue.mixin({
  computed: {
    isEmbeddedInNext: () => {
      return window.isEmbeddedInNext;
    },
    isInIframe: () => {
      return window.self !== window.top;
    },
  },
});

moment.locale('en', {
  relativeTime: {
    future: 'in %s',
    past: '%s ago',
    s: 'a few seconds',
    ss: '%d seconds',
    m: 'a minute',
    mm: '%d minutes',
    h: 'an hour',
    hh: '%d hours',
    d: 'a day',
    dd: '%d days',
    w: 'a week',
    ww: '%d weeks',
    M: 'a month',
    MM: '%d months',
    y: 'a year',
    yy: '%d years',
  },
});

// Posthog
// posthog.init('phc_LdizL8pP1phIt73FCXCK9uuYnQ0R2JluBRyY33ZL1nZ', {
//   api_host: 'https://app.posthog.com',
//   maskAllInputs: true,
//   maskInputFn: (text, _element) => '*'.repeat(text.length),
// });

// Sentry Error Tracking
Sentry.init({
  Vue,
  dsn: process.env.SENTRY_DSN,
  integrations: [
    new BrowserTracing({
      routingInstrumentation: Sentry.vueRouterInstrumentation(router),
      tracePropagationTargets: ['localhost', 'soraban.com', /^\//],
    }),
  ],
  // Attach stacktraces to messages as well as exceptions
  attachStacktrace: true,
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,
});

Vue.use(Vuelidate);
Vue.use(Vuex);
Vue.use(VueAxios, axios);
Vue.use(VueQuillEditor);

Vue.config.productionTip = process.env.NODE_ENV === 'production';
// NOTE: uncomment this if you're getting way too many warnings in the console
// Vue.config.silent = true;
Vue.use(Vue2Filters);
Vue.filter('date', date => moment(date).format('LLL'));
Vue.filter('dateOnly', date => moment(date).format('LL'));

Vue.use(VueChartkick, { adapter: Chart });
Vue.use(Vuetify, {
  theme: {
    primary: '#4F7EEA',
    secondary: '#6F839C',
    darkgrey: '#616161',
    darkyellow: '#CDCD00',
    accent: '#4F7EEA',
    error: '#FA4C4F',
    success: '#64BE79',
    warning: '#FABE01',
    'green-accent': '#24ccb8',
  },
});

Vue.filter('kb', val => {
  return Math.floor(val / 1024);
});

Vue.use(VueI18n);

const requireComponent = require.context(
  // The relative path of the components folder
  './components/global',
  // Whether or not to look in subfolders
  false,
  // The regular expression used to match base component filenames
  /Soraban[A-Z]\w+\.(vue)$/,
);

requireComponent.keys().forEach(fileName => {
  // Get component config
  const componentConfig = requireComponent(fileName);

  // Get PascalCase name of component
  const componentName = upperFirst(
    camelCase(
      // Gets the file name regardless of folder depth
      fileName
        .split('/')
        .pop()
        .replace(/\.\w+$/, ''),
    ),
  );

  // Register component globally
  Vue.component(
    componentName,
    // Look for the component options on `.default`, which will
    // exist if the component was exported with `export default`,
    // otherwise fall back to module's root.
    componentConfig.default || componentConfig,
  );
});

router.beforeEach(async (to, from, next) => {
  if (to.meta.superAdminOnly) {
    // super admin pages
    const user = await store.dispatch('getFirebaseUser');

    if (isEmpty(user)) {
      next({
        path: '/signin',
        query: {
          redirect: to.fullPath,
          email: to.query.email,
        },
      });
    } else {
      const currentUser = await store.dispatch('getCurrentUser');

      loadHubSpotWidget(currentUser);

      if (currentUser.is_advisor && currentUser.email_verified) {
        next();
      } else {
        store.dispatch('application/setErrorNotification', {
          message: "You don't have access to that page.",
        });
        next('/overview');
      }
    }
  } else if (
    to.matched.some(record => record.meta.requiresAuth && record.meta.advisor) // advisor pages
  ) {
    const user = await store.dispatch('getFirebaseUser');
    if (isEmpty(user)) {
      next({
        path: '/signin',
        query: {
          redirect: to.fullPath,
          email: to.query.email,
        },
      });
    } else {
      const currentUser = await store.dispatch('getCurrentUser');

      loadHubSpotWidget(currentUser);

      if (currentUser.is_advisor) {
        next();
      } else {
        store.dispatch('application/setErrorNotification', {
          message: "You don't have access to that page.",
        });
        next('/');
      }
    }
  } else if (to.meta.requiresAuth) {
    // member pages
    const user = await store.dispatch('getFirebaseUser');
    if (isEmpty(user)) {
      next({
        path: '/signin',
        query: {
          redirect: to.fullPath,
          email: to.query.email,
        },
      });
    } else {
      const currentUser = await store.dispatch('getCurrentUser');
      if (currentUser.is_advisor) {
        if (to.path === '/') {
          next({ path: '/overview' });
        }
        if (window.Intercom) {
          window.Intercom('update');
        }
      }

      next();
    }
  } else {
    next();
  }
});

let app;

firebase.auth().onAuthStateChanged(async currentUser => {
  let user;

  if (!isEmbedded()) {
    if (currentUser) {
      await store.dispatch('getFirebaseUser');
      const token = await store.dispatch('getToken');
      if (token) {
        // Prevents 401 errors during 2FA flow
        user = await store.dispatch('getCurrentUser');

        await store.commit('setUser', user);
        if (isEmpty(user)) {
          store.dispatch('userSignOut');
        }

        // posthog.identify(user.id, {
        //   name: user.full_name,
        //   email: user.email,
        //   is_advisor: user.is_advisor,
        //   for_testing: user.for_testing,
        //   paying: user.paying,
        //   preferred_sign_in_method: user.preferred_sign_in_method,
        //   passwordless: user.passwordless,
        //   set_password_required: user.set_password_required,
        //   assigned_advisor_id: user.assigned_advisor_id,
        // });

        let currentFirm = null;

        store.dispatch('companies/getCompanies');

        store.dispatch('setDefaultCompany', {
          companyId: user.default_company_id,
        });
        currentFirm = await store.dispatch('setCurrentFirm');

        // posthog.group('firm', currentFirm.id, {
        //   name: currentFirm.name,
        // });

        store.dispatch('resetTiles');

        if (!user.is_advisor && currentFirm && currentFirm.theme) {
          Vue.prototype.$vuetify.theme = Object.assign(
            Vue.prototype.$vuetify.theme,
            currentFirm.theme,
          );
        }
      }
    } else {
      store.dispatch('userSignOut');
    }
  }

  const selectedLanguage =
    user && user.language ? user.language : navigator.language;
  if (!app) {
    const i18n = new VueI18n({
      locale: selectedLanguage,
      messages: languages,
      silentTranslationWarn: true,
    });

    /* eslint-disable no-new */
    app = new Vue({
      el: '#app',
      i18n,
      router,
      store,
      components: { App },
      template: '<App/>',
    });
  } else {
    app.$i18n.locale = selectedLanguage;
  }
});

function isEmbedded() {
  const parsed = parse(window.location.href);

  return parsed.pathname === '/embed';
}

function loadHubSpotWidget(currentUser) {
  if (
    window.innerWidth >= 1000 &&
    currentUser.is_advisor &&
    !window.isEmbeddedInNext
  ) {
    var script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.defer = true;
    script.id = 'hs-script-loader';
    script.src = '//js-na1.hs-scripts.com/44838654.js';

    var firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode.insertBefore(script, firstScript);

    script.onload = function() {
      if (window.HubSpotConversations) {
        window.HubSpotConversations.widget.load();
      }
    };
  }
}
